import React, { useEffect, useRef, useState } from "react";
import { Search, Loader2, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { avatarSrc } from "@/lib/defaults";
import { imageVariantFallback } from "@/lib/mediaUrl";
import { PagerBar, ADMIN_PAGE_SIZE } from "@/components/admin/usePager";

export const Person = ({ u }) => (
  <span className="inline-flex items-center gap-1.5 min-w-0">
    <img src={avatarSrc(u)} alt="" className="h-6 w-6 rounded-full object-cover" onError={(e) => imageVariantFallback(e.currentTarget)} />
    <span className="truncate text-sm font-semibold">{u?.name || "—"}</span>
  </span>
);

// Lot 785 — « Meilleurs parrains » : classement complet (plus limité à 10), recherche par nom / e-mail,
// pagination serveur (25 par page, barre standard de la console « 1–25 sur 212 · page 1/9 »).
const PAGE = ADMIN_PAGE_SIZE;

export const TopReferrersCard = () => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, total_all: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);
  const fetchPage = async (page, query) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/referral/top", { params: { q: query, page, limit: PAGE } });
      setItems(data.items);
      setMeta({ total: data.total, total_all: data.total_all, page: data.page, pages: data.pages });
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fetchPage(1, q), q ? 300 : 0);
    return () => clearTimeout(timer.current);
  }, [q]);
  return (
    <div className="yalola-card p-5" data-testid="referral-top">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="font-bold font-display inline-flex items-center gap-1.5"><Trophy size={16} style={{ color: "var(--yalola-primary)" }} /> Meilleurs parrains</div>
        <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="referral-top-count">
          {q ? `${meta.total} résultat(s) sur ${meta.total_all} parrain(s)` : `${meta.total_all} parrain(s) avec au moins 1 invité validé`}
        </span>
      </div>
      <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--yalola-text-muted)" }} />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un parrain (nom ou e-mail)…" className="pl-9 h-9" aria-label="Rechercher un parrain" data-testid="referral-top-search" />
      </div>
      {items.length === 0 && !loading ? (
        <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="referral-top-empty">{q ? "Aucun parrain ne correspond." : "Aucun invité validé pour l'instant."}</p>
      ) : (
        <div className="space-y-2" data-testid="referral-top-list">
          {items.map((r) => (
            <div key={r.user?.id || r.rank} className="flex items-center gap-2" data-testid={`referral-top-${r.rank}`}>
              <span className="text-xs font-bold w-7 shrink-0 text-right" style={{ color: r.rank <= 3 ? "var(--yalola-primary)" : "var(--yalola-text-muted)" }}>{r.rank}.</span>
              <Person u={r.user} />
              <span className="ml-auto text-xs text-right shrink-0" style={{ color: "var(--yalola-text-muted)" }}>{r.validated} validé(s) · {r.points} pts gagnés · solde {r.balance}</span>
            </div>
          ))}
        </div>
      )}
      {loading && <div className="mt-3 flex justify-center" data-testid="referral-top-loading"><Loader2 size={16} className="animate-spin" style={{ color: "var(--yalola-text-muted)" }} /></div>}
      <PagerBar page={meta.page} pages={meta.pages} total={meta.total} shown={items.length} onPage={(p) => fetchPage(p, q)} noun="parrain" testId="referral-top-pager" />
    </div>
  );
};
